import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ExtensionDownload from '../components/network/ExtensionDownload';
import ServerAddress from '../components/network/ServerAddress';
import AccessTokens from '../components/network/AccessTokens';
import Panel from '../components/network/Panel';
import { InstallGuideLink } from '../components/network/InstallGuide';
import { SharedBadge } from '../components/network/Badges';
import ConfirmShareDialog from '../components/network/ConfirmShareDialog';
import ConsoleForOthersToggle from '../components/network/ConsoleForOthersToggle';
import type { PluginDevice } from '../types';

function fmtDate(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

/** The queued extension: setup, tokens and devices. It checks in every 30
 *  seconds, picks up queued fetch jobs and sends the results back, so it keeps
 *  working through a backlog while nobody is watching.
 *
 *  Admins can SHARE one of their own browsers with all users, as on the
 *  connected card: it then also picks up the queued jobs of anyone with no
 *  browser of their own online. Sharing asks first - see ConfirmShareDialog. */
/** `showTokens` off where the page shows the token section once for both cards. */
export default function QueuedExtensionSettings({ showTokens = true }: { showTokens?: boolean } = {}) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [devices, setDevices] = useState<PluginDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState<PluginDevice | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDevices(await api.getPluginDevices());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the queued extension devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Sharing goes through the dialog (the server refuses it without confirm_shared);
  // un-sharing is immediate - taking a browser back is never the risky direction.
  const setShared = async (d: PluginDevice, shared: boolean) => {
    setError('');
    setBusy(true);
    try {
      const saved = await api.setPluginDeviceSystem(d.id, shared, shared);
      setDevices((all) => all.map((x) => (x.id === saved.id ? saved : x)));
      setSharing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update device');
    } finally {
      setBusy(false);
    }
  };

  const setConsoleForOthers = async (d: PluginDevice, on: boolean) => {
    setError('');
    setBusy(true);
    try {
      const saved = await api.setPluginDeviceConsoleForOthers(d.id, on);
      setDevices((all) => all.map((x) => (x.id === saved.id ? saved : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update device');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-500 hover:text-red-700 dark:hover:text-red-300">&times;</button>
        </div>
      )}

      {/* Who is connected first: it is what this tab is opened to check. */}
      <Panel
        title="Devices"
        hint="Green while online. Each one checks in every 30 seconds."
        actions={
          <button onClick={fetchDevices} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">Refresh</button>
        }
      >
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading…</div>
          ) : devices.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No device has connected yet. <InstallGuideLink build="chrome-queued">How to install</InstallGuideLink>
            </div>
          ) : (
            devices.map((d) => (
              <div key={d.id} className="px-4 py-3" data-testid={`queued-device-${d.id}`}>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${d.online ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {d.name || d.device_id}
                  {d.is_system && <SharedBadge />}
                  {isAdmin && (
                    <button
                      onClick={() => (d.is_system ? setShared(d, false) : setSharing(d))}
                      className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {d.is_system ? 'Stop sharing' : 'Share with all users…'}
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {d.online ? 'Online' : 'Offline'} · Last seen {fmtDate(d.last_seen_at)} · Max tabs {d.max_tabs}
                </div>
                {isAdmin && d.is_system && (
                  <ConsoleForOthersToggle
                    on={!!d.console_for_others}
                    busy={busy}
                    onChange={(on) => setConsoleForOthers(d, on)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </Panel>

      {showTokens && <AccessTokens />}

      <Panel
        title="Get the queued extension"
        hint="Orange dot on its icon. Works through waiting fetches, even while you're away."
        actions={
          <>
            <InstallGuideLink build="chrome-queued">How to install</InstallGuideLink>
            <InstallGuideLink build="firefox-queued">Firefox</InstallGuideLink>
          </>
        }
      >
        <div className="p-4 space-y-4">
          <ExtensionDownload kind="queued" />
          <ServerAddress />
        </div>
      </Panel>

      <ConfirmShareDialog
        isOpen={sharing !== null}
        deviceName={sharing?.name || sharing?.device_id || 'this browser'}
        busy={busy}
        onCancel={() => setSharing(null)}
        onConfirm={() => sharing && setShared(sharing, true)}
      />
    </div>
  );
}
